import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { SectionCard } from './SectionCard'
import { RadialGauge } from './RadialGauge'
import { Skeleton } from '@/components/ui/skeleton'
import { useDashboardOverview } from '@/hooks/useDashboard'
import { useDashboardPeriod } from './DashboardPeriodContext'
import { cn } from '@/lib/utils'

function QuoteRateIcon() {
  return (
    <svg
      width='16'
      height='16'
      viewBox='0 0 16 16'
      fill='none'
      xmlns='http://www.w3.org/2000/svg'
      aria-hidden
    >
      <g transform='translate(1.66665 0.33335)'>
        <path
          d='M11.6667 5.66667L7 1H2.33333C1.97971 1 1.64057 1.14048 1.39052 1.39052C1.14048 1.64057 1 1.97971 1 2.33333V13C1 13.3536 1.14048 13.6928 1.39052 13.9428C1.64057 14.1929 1.97971 14.3333 2.33333 14.3333H10.3333C10.687 14.3333 11.0261 14.1929 11.2761 13.9428C11.5262 13.6928 11.6667 13.3536 11.6667 13V5.66667ZM7 1V5.66667H11.6667'
          stroke='#059669'
          strokeWidth='2'
          strokeLinecap='round'
          strokeLinejoin='round'
        />
      </g>
    </svg>
  )
}

function CompletedRateIcon() {
  return (
    <svg
      width='16'
      height='16'
      viewBox='0 0 16 16'
      fill='none'
      xmlns='http://www.w3.org/2000/svg'
      aria-hidden
    >
      <g transform='translate(0.66665 1)'>
        <path
          d='M5 6.33333L7 8.33333L13.6667 1.66667M13 7V11.6667C13 12.0203 12.8595 12.3594 12.6095 12.6095C12.3594 12.8595 12.0203 13 11.6667 13H2.33333C1.97971 13 1.64057 12.8595 1.39052 12.6095C1.14048 12.3594 1 12.0203 1 11.6667V2.33333C1 1.97971 1.14048 1.64057 1.39052 1.39052C1.64057 1.14048 1.97971 1 2.33333 1H9.66667'
          stroke='#2550CC'
          strokeWidth='2'
          strokeLinecap='round'
          strokeLinejoin='round'
        />
      </g>
    </svg>
  )
}

function RateDeltaArrow({ up }: { up: boolean }) {
  return (
    <svg
      width='14'
      height='14'
      viewBox='0 0 14 14'
      fill='none'
      xmlns='http://www.w3.org/2000/svg'
      className={cn('size-3.5 shrink-0', !up && 'rotate-180')}
      aria-hidden
    >
      <g transform='translate(2.11665 2.11665)'>
        <path
          d='M4.88333 8.96667V0.8M8.96667 4.88333L4.88333 0.8L0.8 4.88333'
          stroke='currentColor'
          strokeWidth='1.6'
          strokeLinecap='round'
          strokeLinejoin='round'
        />
      </g>
    </svg>
  )
}

function RateActionArrow() {
  return (
    <svg
      width='16'
      height='16'
      viewBox='0 0 16 16'
      fill='none'
      xmlns='http://www.w3.org/2000/svg'
      aria-hidden
    >
      <g transform='translate(2.53335 2.53335)'>
        <path
          d='M0.8 5.46667H10.1333M5.46667 10.1333L10.1333 5.46667L5.46667 0.8'
          stroke='#FF5A1F'
          strokeWidth='1.6'
          strokeLinecap='round'
          strokeLinejoin='round'
        />
      </g>
    </svg>
  )
}

interface RateOverview {
  quote_signature_rate?: number | null
  quote_signature_rate_delta?: number | null
  quote_signature_count?: number
  quote_signature_total?: number
  completed_interventions_rate?: number | null
  completed_interventions_rate_delta?: number | null
  completed_interventions_count?: number
  completed_interventions_total?: number
}

interface RatePanelProps {
  title: string
  icon: React.ReactNode
  iconClassName: string
  value: number | null
  delta: number | null | undefined
  headline: string
  description: string
  action: { label: string; to: string }
  loading?: boolean
  error?: boolean
}

function RateDelta({ value }: { value: number | null | undefined }) {
  const { t } = useTranslation('dashboard')
  if (value === null || value === undefined) {
    return (
      <p className='text-center text-sm text-muted-foreground'>
        {t('delta.noPriorData')}
      </p>
    )
  }
  const up = value >= 0
  return (
    <div className='flex items-center gap-1.5'>
      <span
        className={cn(
          'inline-flex items-center gap-1 py-0.5 font-sans text-xs font-medium leading-4',
          up ? 'text-[#065f46]' : 'text-destructive'
        )}
      >
        <RateDeltaArrow up={up} />
        {Math.abs(Math.round(value))}%
      </span>
      <span className='font-sans text-xs font-normal leading-[1.4] text-[#3d434e]'>
        {t('rates.vsPreviousMonth')}
      </span>
    </div>
  )
}

function RatePanel({
  title,
  icon,
  iconClassName,
  value,
  delta,
  headline,
  description,
  action,
  loading,
  error,
}: RatePanelProps) {
  const { t } = useTranslation('dashboard')

  return (
    <SectionCard
      variant='chart'
      title={title}
      className='h-70.5'
      headerClassName='min-h-16'
      contentClassName='h-54.5'
      headerIcon={icon}
      headerIconClassName={iconClassName}
    >
      {loading ? (
        <Skeleton className='size-35 shrink-0 rounded-full' />
      ) : error ? (
        <p className='flex h-full items-center justify-center text-center text-sm text-destructive'>
          {t('rates.unableToLoad')}
        </p>
      ) : (
        <div className='flex h-full w-full items-start gap-6 pb-7.5'>
          <RadialGauge value={value} />
          <div className='flex h-full min-w-0 flex-1 flex-col items-start gap-4'>
            <div className='flex w-full flex-col gap-1'>
              <p className='font-heading text-sm font-semibold leading-[1.2] text-[#171a20]'>
                {headline}
              </p>
              <p className='font-sans text-xs font-normal leading-[1.4] text-[#6c7285]'>
                {description}
              </p>
            </div>
            <RateDelta value={delta} />
            <Link
              to={action.to}
              className='mt-auto inline-flex items-center gap-2 rounded-lg p-2 font-sans text-xs font-medium leading-none text-[#ff5a1f] hover:underline'
            >
              {action.label}
              <RateActionArrow />
            </Link>
          </div>
        </div>
      )}
    </SectionCard>
  )
}

export function QuoteSignOffRate() {
  const { t } = useTranslation('dashboard')
  const { period } = useDashboardPeriod()
  const { data, isLoading, isError } = useDashboardOverview(period)
  const o = data?.data as (RateOverview | null) | undefined
  return (
    <RatePanel
      title={t('rates.quoteSignOffTitle')}
      icon={<QuoteRateIcon />}
      iconClassName='bg-[#ecfdf5]'
      value={o?.quote_signature_rate ?? null}
      delta={o?.quote_signature_rate_delta}
      headline={t('rates.quoteHeadline', {
        count: o?.quote_signature_count ?? 0,
        signed: o?.quote_signature_count ?? 0,
        total: o?.quote_signature_total ?? 0,
      })}
      description={t('rates.quoteDescription')}
      action={{
        label: t('rates.viewSignedQuotes'),
        to: '/quotes?status=won',
      }}
      loading={isLoading}
      error={isError}
    />
  )
}

export function CompletedInterventionsRate() {
  const { t } = useTranslation('dashboard')
  const { period } = useDashboardPeriod()
  const { data, isLoading, isError } = useDashboardOverview(period)
  const o = data?.data as (RateOverview | null) | undefined
  return (
    <RatePanel
      title={t('rates.completedInterventionsTitle')}
      icon={<CompletedRateIcon />}
      iconClassName='bg-[#ebf1fe]'
      value={o?.completed_interventions_rate ?? null}
      delta={o?.completed_interventions_rate_delta}
      headline={t('rates.completedHeadline', {
        completed: o?.completed_interventions_count ?? 0,
        total: o?.completed_interventions_total ?? 0,
      })}
      description={t('rates.completedDescription')}
      action={{
        label: t('rates.viewCompletedInterventions'),
        to: '/calendar?status=completed',
      }}
      loading={isLoading}
      error={isError}
    />
  )
}
